import throttle from "just-throttle";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import OverlayMenu from "@lib/beskar-overlay-menu/bskr-overlay-menu.mjs";

// Layout-level behaviour that can't ship as a per-block script (nested layout
// assets aren't reliably enqueued): scroll reveals for Banner + Assets and
// Gallery + Text.
import "./partials/banner-assets.js";
import "./partials/gallery-text-reveal.js";
import "./partials/lazy-video.js";
import "./partials/shop-locator.js";
import "./partials/three-col-masonry-gallery.js";
import "./partials/display-reveal-text.js";
import "./partials/figures-cards.js";
import "./partials/hero-subcategory.js";
import "./partials/video-card.js";
import "./partials/zigzag-text-media.js";
import "./partials/connectif-cf7.js";

const adminBarOffset = parseInt(
  getComputedStyle(document.documentElement).getPropertyValue(
    "--wp-admin--admin-bar--height"
  )
);

function updateAdminBarOffset(visible) {
  const header = document.getElementById("header");
  const fixedElement = document.querySelector(".is-position-sticky");

  if (fixedElement) {
    if (visible) {
      fixedElement.style.setProperty(
        "--bskr--header-height",
        header.offsetHeight + "px"
      );
    } else {
      fixedElement.style.setProperty("--bskr--header-height", 0 + "px");
    }
  }
}

function initLenisScroll() {
  const lenis = new Lenis({
    prevent: function (element) {
      return element.classList.contains("no-lenis");
    },
  });
  window.lenisInstance = lenis;

  lenis.on("scroll", ScrollTrigger.update);

  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);

  return { lenis, addEventListener, gsapTicker: gsap.ticker };
}

let isHeaderHidden = false;

function toggleHeaderOnMenu(
  header,
  args = { duration: 0.5, ease: "power4.out" }
) {
  document.body.classList.toggle("on-menu-opened");
  const isHidden = toggleHeader(header, args);
  gsap.to(header, {
    yPercent: isHidden ? -100 : 0,
    ...args,
  });
}

function showHeader(header, args = { duration: 0.5, ease: "power4.out" }) {
  gsap.to(header, {
    yPercent: 0,
    ...args,
  });
}

function hideHeader(header, args = { duration: 0.5, ease: "power4.out" }) {
  gsap.to(header, {
    yPercent: -100,
    ...args,
  });
}

function toggleHeader(header, args = { duration: 0.5, ease: "power4.out" }) {
  isHeaderHidden = !isHeaderHidden;
  !isHeaderHidden ? hideHeader(header, args) : showHeader(header, args);
  return isHeaderHidden;
}

document.addEventListener("DOMContentLoaded", () => {
  gsap.registerPlugin(gsap.ScrollTrigger);

  const isDesktop = window.innerWidth >= 1024;

  let lenis = isDesktop ? initLenisScroll().lenis : null;

  const header = document.getElementById("header");
  const overlayMenu = new OverlayMenu("main", { options: { duration: 1 } });

  if (overlayMenu) {
    overlayMenu.addEvent(
      "onOpen",
      throttle(() => {
        lenis?.stop();
        toggleHeaderOnMenu(header);
      }, 10)
    );

    overlayMenu.addEvent(
      "onClosed",
      throttle(() => {
        lenis?.start();
        toggleHeaderOnMenu(header);
      }, 10)
    );
  }

  window.showHeader = () => showHeader(header);
  window.hideHeader = () => hideHeader(header);
  window.toggleHeader = () => toggleHeader(header);

  // Language switcher — custom flag dropdown (template-parts/shared/language-selector.php)
  document.querySelectorAll("[data-lang-switch]").forEach((root) => {
    const trigger = root.querySelector(".lang-switch__trigger");
    const menu = root.querySelector(".lang-switch__menu");
    if (!trigger || !menu) return;

    const close = () => {
      root.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      menu.hidden = true;
    };
    const open = () => {
      root.classList.add("is-open");
      trigger.setAttribute("aria-expanded", "true");
      menu.hidden = false;
    };

    trigger.addEventListener("click", (event) => {
      event.stopPropagation();
      menu.hidden ? open() : close();
    });
    document.addEventListener("click", (event) => {
      if (!root.contains(event.target)) close();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });
  });
});

document.addEventListener("DOMContentLoaded", function () {
  if (process.env.NODE_ENV === "development") {
    console.log("Development mode enabled.");
  }
});
